import { ref } from 'vue';
import axios from 'axios';
import { useDebounceFn } from '@vueuse/core';
import { useConfigHelper } from '@ilteducation/config';
import type { KeywordSearchResult, SearchKeywordsResponseBody } from '@ilteducation/word-of-the-day-types';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 20;

/**
 * Debounced keyword search against the public `GET /keywords/search` endpoint on the
 * word-of-the-day service. Results are already filtered server-side to playable words.
 */
const useKeywordSearch = () => {
  const { getApiUrl } = useConfigHelper();

  const query = ref('');
  const results = ref<KeywordSearchResult[]>([]);
  const isLoading = ref(false);
  const error = ref<unknown>(null);

  // Guards against out-of-order responses: only the most recent request may write results.
  let latestRequestId = 0;

  const runSearch = async (q: string) => {
    const requestId = ++latestRequestId;
    isLoading.value = true;
    error.value = null;
    try {
      const response = await axios.get<SearchKeywordsResponseBody>(
        `${getApiUrl('word-of-the-day', true)}/keywords/search`,
        { params: { q, limit: DEFAULT_LIMIT } },
      );
      if (requestId !== latestRequestId) {
        return;
      }
      results.value = response.data.results;
    } catch (e) {
      if (requestId !== latestRequestId) {
        return;
      }
      error.value = e;
      results.value = [];
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  };

  const debouncedSearch = useDebounceFn(runSearch, SEARCH_DEBOUNCE_MS);

  const setQuery = (value: string) => {
    query.value = value ?? '';
    const trimmed = query.value.trim();
    if (!trimmed) {
      // Cancel any in-flight result from landing once the field is cleared.
      latestRequestId += 1;
      results.value = [];
      isLoading.value = false;
      return;
    }
    isLoading.value = true;
    debouncedSearch(trimmed);
  };

  return { query, results, isLoading, error, setQuery };
};

export default useKeywordSearch;
